/**
 * Claves de idempotencia del canal publico (ADR-024).
 *
 * Regla de uso, que es la parte que se rompe: **una clave por intencion de la persona**,
 * no por peticion. Si la red falla y hay que reintentar, se manda la MISMA clave; si la
 * persona cambia de horario o empieza otra reserva, se genera una nueva. Reintentar con
 * clave nueva puede cobrar dos veces; reintentar con la misma no puede.
 *
 * El servidor exige UUID v4 (`functions/src/esquemas/entradas.ts`).
 */

const HEXA = '0123456789abcdef';

/** Bytes aleatorios criptograficos. Sin alternativa insegura: si no hay, se lanza. */
function bytesAleatorios(cantidad: number): Uint8Array {
  const destino = new Uint8Array(cantidad);
  crypto.getRandomValues(destino);
  return destino;
}

function hexa(valor: number): string {
  return `${HEXA[(valor >> 4) & 0x0f] ?? '0'}${HEXA[valor & 0x0f] ?? '0'}`;
}

/**
 * Genera una clave de idempotencia nueva, en formato UUID v4.
 *
 * Usa `crypto.randomUUID` cuando existe. No siempre existe: los navegadores solo lo
 * exponen en contextos seguros, y algunos entornos de prueba lo omiten. El respaldo
 * arma el UUID desde `crypto.getRandomValues`, que si es universal, fijando la version
 * (4) y la variante (10xx) como manda RFC 4122.
 */
export function nuevaClaveIdempotencia(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = bytesAleatorios(16);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const texto = Array.from(bytes, hexa).join('');
  return [
    texto.slice(0, 8),
    texto.slice(8, 12),
    texto.slice(12, 16),
    texto.slice(16, 20),
    texto.slice(20, 32),
  ].join('-');
}
